import * as inputsStyles from "../inputs.module.scss"
import React from "react"
import { RegisterOptions, UseFormRegisterReturn } from "react-hook-form"

type RenderProps = {
  id?: string
  type: string
  name: string
  required?: boolean
  className?: string
  placeholder?: string
  title?: string
  validationType?: ValidationType
  hasErrors?: boolean
  describedBy?: string
  onFocus?: (value: string) => void
  autocomplete?: boolean | string
} & (
  | {
      value?: string
      onChange?: (value: string) => void
      register: (name: string, options?: RegisterOptions) => UseFormRegisterReturn
    }
  | { value: string; onChange: (value: string) => void; register?: undefined }
)

export enum ValidationType {
  None,
  Email,
  PhoneNumber,
  PhoneNumberE164,
  Url,
}

const Input: React.FC<RenderProps> = ({
  id,
  type,
  name,
  required,
  className = "",
  placeholder = "",
  title,
  value,
  onChange,
  register,
  validationType,
  hasErrors,
  describedBy,
  onFocus,
  autocomplete,
}) => {
  const validation = {
    required,
    pattern: getValidationPattern(validationType),
  }

  const sharedInputProps = {
    id,
    type,
    name,
    placeholder,
    title,
    required,
    autoComplete: getAutoComplete(validationType, autocomplete, name),
    "aria-describedby": describedBy || undefined,
    "aria-invalid": hasErrors ? true : undefined,
    className:
      inputsStyles.input +
      " " +
      (isNotBlank(value) ? inputsStyles.notBlank + " " : "") +
      (hasErrors ? inputsStyles.fieldInError + " " : "") +
      className,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(event.target.value)
    },
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus && onFocus(event.target.id)
    },
  }

  return register != undefined ? (
    <input {...sharedInputProps} {...register(name, { ...validation })} value={value} />
  ) : (
    <input {...sharedInputProps} value={value} />
  )
}

function getAutoComplete(
  validationType: ValidationType | undefined,
  autocomplete: boolean | string | undefined,
  name: string
) {
  switch (validationType) {
    case ValidationType.PhoneNumber:
      return "tel-national"
    case ValidationType.Email:
      return "email"
    case ValidationType.PhoneNumberE164:
      return "tel"
    case ValidationType.Url:
      return "url"
    default:
      break
  }

  if (typeof autocomplete === "string") {
    return autocomplete
  }

  if (autocomplete === true && isStandardAutoCompleteName(name)) {
    return name
  }

  return null
}

function isStandardAutoCompleteName(name: string) {
  return /^[a-z0-9-]+$/.test(name) && !name.toLowerCase().includes("datocms")
}

function getValidationPattern(validationType: ValidationType) {
  switch (validationType) {
    case ValidationType.Email:
      return /.+@.+/
    case ValidationType.PhoneNumber:
      return /([\s.-]*[0-9][\s.-]*){10}/
    case ValidationType.PhoneNumberE164:
      return /^\+[1-9]\d{1,14}$/
    case ValidationType.Url:
      return /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/
    default:
      return undefined
  }
}

function isNotBlank(value: string) {
  return value && value.trim().length > 0
}

export default Input
